import { Box, Typography, TextField, Button, Link, Alert, Divider, Paper } from '@mui/material'
import { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useRegisterMutation, useGetAuthConfigQuery } from '@store/api/authApi'
import { useBetaMode } from '@hooks/useBetaMode'
import { loginStart, loginSuccess, loginFailure } from '@store/slices/authSlice'
import GoogleOAuthButton from '@components/auth/GoogleOAuthButton'
import { resolvePostAuthPath } from '@utils/authRedirect'

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  })
  const [error, setError] = useState('')
  
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const selectedPlan = searchParams.get('plan')
  const { beta } = useBetaMode()

  const planLabels: Record<string, string> = {
    free: 'Free Plan (Free)',
    premium: 'Premium Plan ($9.99/mo)',
    pro: 'Pro Plan ($19.99/mo)',
  }
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [registerMutation, { isLoading: isRegisterLoading }] = useRegisterMutation()
  const { data: authConfig } = useGetAuthConfigQuery()
  const googleOAuthEnabled = authConfig?.googleOAuth ?? false

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(resolvePostAuthPath(location, searchParams), { replace: true })
    }
  }, [isAuthenticated, navigate, location, searchParams])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    try {
      dispatch(loginStart())
      const result = await registerMutation({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
      }).unwrap()
      
      dispatch(loginSuccess({
        user: result.user,
        token: result.accessToken,
      }))
      
      navigate(resolvePostAuthPath(location, searchParams), { replace: true })
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Registration failed. Please try again.'
      setError(errorMessage)
      dispatch(loginFailure(errorMessage))
    }
  }

  const handleGoogleRegister = () => {
    // Redirect to Google OAuth endpoint (relative path for nginx proxy)
    window.location.href = '/api/v1/auth/google'
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Create Account
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" paragraph>
        Join CigarTrack to start managing your collection
      </Typography>

      {selectedPlan && planLabels[selectedPlan] && (!beta || selectedPlan === 'free') && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            p: 2,
            borderColor: '#DAA520',
            backgroundColor: '#fffbea',
            borderWidth: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Selected plan
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#2F1B14', fontWeight: 600 }}>
            {planLabels[selectedPlan]}
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
          required
        />
        
        <TextField
          fullWidth
          label="Username"
          name="username"
          value={formData.username}
          onChange={handleChange}
          margin="normal"
          required
        />
        
        <TextField
          fullWidth
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          margin="normal"
        />
        
        <TextField
          fullWidth
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          margin="normal"
        />
        
        <TextField
          fullWidth
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          margin="normal"
          required
        />
        
        <TextField
          fullWidth
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          margin="normal"
          required
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isRegisterLoading}
          sx={{ mt: 3, mb: 2 }}
        >
          {isRegisterLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
        
        {googleOAuthEnabled && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <GoogleOAuthButton
              onClick={handleGoogleRegister}
              disabled={isRegisterLoading}
              text="Sign up with Google"
            />
          </>
        )}

        <Box textAlign="center">
          <Link component={RouterLink} to="/auth/login">
            Already have an account? Sign in
          </Link>
        </Box>
      </Box>
    </Box>
  )
}

export default Register
